import {
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Portal,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/** Space to leave below profile content so fixed mobile Save/Cancel is not covering fields (sync with ProfileForm bottom padding). */
export function profileMobileInlineEditBottomReserve(theme: Theme): string {
    return `calc(${theme.spacing(2)} + 72px + ${theme.spacing(2)} + ${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`;
}
import {
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type ProfileSectionCardProps = {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    editButtonLabel: string;
    onEdit: () => void;
    showEditButton: boolean;
    /** When true, inline edit is active (Save/Cancel on mobile sticky bar; desktop footer + chip). */
    inlineEditActive?: boolean;
    onCancelInline?: () => void;
    onSaveInline?: () => void;
    /** When true, Save / Save Changes is disabled (e.g. no form changes in this section). */
    saveDisabled?: boolean;
    /**
     * When true, body starts collapsed; header stays visible. Opens while inline editing;
     * collapses again after save/cancel. Used for contractor profile (services, availability, terms).
     */
    defaultCollapsed?: boolean;
    children: React.ReactNode;
};

const titleBlock = (
    icon: React.ReactNode,
    title: string,
    subtitle: string,
    titleAdornment?: React.ReactNode,
) => (
    <Stack direction='row' spacing={2} alignItems='flex-start'>
        <Box
            sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: (t) =>
                    t.palette.mode === 'dark'
                        ? 'action.selected'
                        : alpha(t.palette.primary.main, 0.12),
                color: 'primary.main',
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            {titleAdornment ? (
                <Stack
                    direction='row'
                    alignItems='center'
                    spacing={1}
                    flexWrap='wrap'
                >
                    <Typography
                        component='h2'
                        variant='h6'
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.3,
                        }}
                    >
                        {title}
                    </Typography>
                    {titleAdornment}
                </Stack>
            ) : (
                <Typography
                    component='h2'
                    variant='h6'
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
            )}
            <Typography
                variant='body2'
                sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                }}
            >
                {subtitle}
            </Typography>
        </Box>
    </Stack>
);

const desktopFooterSaveCancel = (
    onCancelInline: () => void,
    onSaveInline: () => void,
    saveDisabled: boolean,
    theme: Theme,
) => (
    <Box
        sx={{
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: { xs: 2, sm: 2.5 },
            borderTop: `1px solid ${theme.palette.divider}`,
        }}
    >
        <Stack
            direction='row'
            spacing={1}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
        >
            <Button
                type='button'
                variant='outlined'
                size='small'
                onClick={onCancelInline}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'divider',
                    color: 'text.primary',
                }}
            >
                Cancel
            </Button>
            <Button
                type='button'
                variant='contained'
                size='small'
                disabled={saveDisabled}
                onClick={onSaveInline}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                }}
            >
                Save Changes
            </Button>
        </Stack>
    </Box>
);

export default function ProfileSectionCard({
    icon,
    title,
    subtitle,
    editButtonLabel,
    onEdit,
    showEditButton,
    inlineEditActive = false,
    onCancelInline,
    onSaveInline,
    saveDisabled = false,
    defaultCollapsed = false,
    children,
}: ProfileSectionCardProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:770px)');

    const accentEditing =
        inlineEditActive && Boolean(onCancelInline) && Boolean(onSaveInline);

    const [sectionExpanded, setSectionExpanded] = useState(
        () => !defaultCollapsed,
    );
    const prevInlineEditActive = useRef(inlineEditActive);

    useEffect(() => {
        if (inlineEditActive) {
            setSectionExpanded(true);
        }
    }, [inlineEditActive]);

    useLayoutEffect(() => {
        if (
            defaultCollapsed &&
            prevInlineEditActive.current &&
            !inlineEditActive
        ) {
            setSectionExpanded(false);
        }
        prevInlineEditActive.current = inlineEditActive;
    }, [inlineEditActive, defaultCollapsed]);

    const bodyOpen = !defaultCollapsed || sectionExpanded || inlineEditActive;

    const collapsibleToggle =
        defaultCollapsed && !inlineEditActive ? (
            <Tooltip
                title={sectionExpanded ? 'Collapse' : 'Expand'}
                placement='top'
            >
                <IconButton
                    type='button'
                    size='small'
                    onClick={() => setSectionExpanded((v) => !v)}
                    aria-expanded={sectionExpanded}
                    aria-label={
                        sectionExpanded
                            ? `Collapse ${title}`
                            : `Expand ${title}`
                    }
                    sx={{
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        color: 'text.secondary',
                    }}
                >
                    <ExpandMoreIcon
                        sx={{
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.shortest,
                            }),
                            transform: sectionExpanded
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                        }}
                    />
                </IconButton>
            </Tooltip>
        ) : null;

    const editingSurface = isMobile && accentEditing;

    const editingStatusChip = accentEditing ? (
        <Chip
            label='Editing'
            size='small'
            variant='outlined'
            sx={{
                flexShrink: 0,
                fontWeight: 600,
                letterSpacing: 0.02,
                color: 'text.secondary',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
            }}
        />
    ) : null;

    const mobileEditChip = editingSurface && editingStatusChip;

    const mobileEditButton = isMobile &&
        showEditButton &&
        !inlineEditActive && (
            <Tooltip title={editButtonLabel}>
                <IconButton
                    type='button'
                    size='small'
                    onClick={onEdit}
                    aria-label={editButtonLabel}
                    sx={{
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        color: 'text.primary',
                    }}
                >
                    <EditIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
        );

    return (
        <>
            <Paper
                component='section'
                aria-label={title}
                elevation={0}
                sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                {isMobile ? (
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='flex-start'
                        spacing={1}
                        sx={{
                            p: 2,
                            pb: 1.5,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                            {titleBlock(icon, title, subtitle)}
                        </Box>
                        <Stack
                            direction='row'
                            alignItems='flex-start'
                            spacing={0.5}
                            sx={{ flexShrink: 0 }}
                        >
                            {inlineEditActive && onCancelInline && onSaveInline
                                ? mobileEditChip
                                : mobileEditButton}
                            {collapsibleToggle}
                        </Stack>
                    </Stack>
                ) : (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                        justifyContent='space-between'
                        spacing={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            pb: { xs: 1.5, sm: 2 },
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        {titleBlock(
                            icon,
                            title,
                            subtitle,
                            !isMobile && accentEditing
                                ? editingStatusChip
                                : undefined,
                        )}
                        <Stack
                            direction='row'
                            alignItems='flex-start'
                            spacing={0.5}
                            sx={{ flexShrink: 0 }}
                        >
                            {!accentEditing && showEditButton && (
                                <Tooltip title={editButtonLabel}>
                                    <IconButton
                                        type='button'
                                        size='small'
                                        onClick={onEdit}
                                        aria-label={editButtonLabel}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            flexShrink: 0,
                                            color: 'text.primary',
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {collapsibleToggle}
                        </Stack>
                    </Stack>
                )}
                <Collapse in={bodyOpen} timeout='auto' unmountOnExit={false}>
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            pb: { xs: 2, sm: 3 },
                            pt: { xs: 2, sm: 3 },
                        }}
                    >
                        {children}
                    </Box>
                </Collapse>
                {accentEditing &&
                    !isMobile &&
                    onCancelInline &&
                    onSaveInline &&
                    desktopFooterSaveCancel(
                        onCancelInline,
                        onSaveInline,
                        saveDisabled,
                        theme,
                    )}
            </Paper>
            {editingSurface && onCancelInline && onSaveInline && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar + 1,
                            bgcolor: 'background.paper',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            px: 2,
                            py: 2,
                            pb: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                        }}
                    >
                        <Stack
                            direction='row'
                            spacing={1}
                            sx={{ width: '100%' }}
                        >
                            <Button
                                type='button'
                                variant='outlined'
                                size='medium'
                                onClick={onCancelInline}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    py: 1.25,
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type='button'
                                variant='contained'
                                size='medium'
                                disabled={saveDisabled}
                                onClick={onSaveInline}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    py: 1.25,
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Save Changes
                            </Button>
                        </Stack>
                    </Box>
                </Portal>
            )}
        </>
    );
}
